/**
 * HubVisualization — Animated ASCII model routing visualization.
 *
 * Shows the Cortex Hub evaluating providers and selecting the optimal model
 * for a given task. Providers appear one by one, "evaluating..." types out,
 * winning provider highlights, then decision text appears.
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { HubEvent } from '../../data/cortex-adventure';
import { PROVIDERS } from '../../data/cortex-adventure';

interface Props {
  event: HubEvent;
  onComplete?: () => void;
}

// ── Provider card for the visualization ──────────────────

const ProviderCard = ({
  name,
  model,
  cost,
  isWinner,
  isEvaluating,
  delay,
  color,
  borderColor,
}: {
  name: string;
  model: string;
  cost: string;
  isWinner: boolean;
  isEvaluating: boolean;
  delay: number;
  color: string;
  borderColor: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.3 }}
    className={`
      px-3 py-2 rounded border font-mono text-[11px] min-w-[120px] transition-all duration-500
      ${isWinner
        ? `${borderColor} bg-white/[0.04] ring-1 ring-white/10`
        : 'border-gray-800/50 bg-white/[0.01]'
      }
    `}
  >
    <div className={`font-semibold ${isWinner ? color : 'text-gray-500'}`}>{name}</div>
    <div className={`${isWinner ? 'text-gray-300' : 'text-gray-600'} text-[10px]`}>{model}</div>
    <div className={`text-[10px] ${isWinner ? 'text-gray-400' : 'text-gray-700'}`}>{cost}</div>
    {isWinner && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-[10px] text-amber-400 mt-1 font-semibold"
      >
        ROUTED
      </motion.div>
    )}
    {isEvaluating && !isWinner && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0.3, 0.8, 0.3] }}
        transition={{ duration: 1.2, repeat: Infinity }}
        className="text-[10px] text-gray-600 mt-1"
      >
        evaluating...
      </motion.div>
    )}
  </motion.div>
);

// ── Main Hub Visualization ───────────────────────────────

export const HubVisualization = ({ event, onComplete }: Props) => {
  const [phase, setPhase] = useState<'loading' | 'evaluating' | 'decided'>('loading');

  // Find relevant providers for the event
  const relevantProviders = PROVIDERS.filter((p) =>
    p.name === event.selectedProvider
    || (event.fallback && p.name === event.fallback.split(' ')[0])
    || p.models.some((m) => m.strengths.includes(event.task.split(':')[0]?.trim().toLowerCase() ?? ''))
  ).slice(0, 4);

  // If we didn't get enough, just show first 3 providers + the selected one
  const displayProviders = relevantProviders.length >= 3
    ? relevantProviders
    : [
        ...PROVIDERS.filter((p) => p.name === event.selectedProvider),
        ...PROVIDERS.filter((p) => p.name !== event.selectedProvider).slice(0, 2),
      ];

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('evaluating'), 600);
    const t2 = setTimeout(() => {
      setPhase('decided');
      onComplete?.();
    }, 2400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onComplete]);

  const savingsPercent = event.baselineCost > 0
    ? Math.round((1 - event.cost / event.baselineCost) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="my-4 rounded-lg border border-gray-800/60 bg-[#0d0d1a] overflow-hidden font-mono"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-800/40 bg-gray-900/30">
        <motion.div
          className="w-1.5 h-1.5 rounded-full bg-amber-400"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
        <span className="text-[10px] text-gray-500 uppercase tracking-widest">Cortex Hub</span>
        <span className="text-[10px] text-gray-700 ml-auto">
          {phase === 'loading' && 'initializing...'}
          {phase === 'evaluating' && 'routing task...'}
          {phase === 'decided' && 'routed'}
        </span>
      </div>

      <div className="p-4">
        {/* Task description */}
        <div className="text-[11px] text-gray-400 mb-3">
          Task: <span className="text-gray-300">{event.task}</span>
          <span className="text-gray-700 ml-2">[{event.complexity}]</span>
        </div>

        {/* Provider cards */}
        <AnimatePresence>
          {(phase === 'evaluating' || phase === 'decided') && (
            <div className="flex flex-wrap gap-2 mb-3">
              {displayProviders.map((provider, idx) => {
                const isWinner = provider.name === event.selectedProvider;
                const primaryModel = provider.models[0];
                return (
                  <ProviderCard
                    key={provider.name}
                    name={provider.name}
                    model={isWinner ? event.selectedModel : (primaryModel?.displayName ?? '—')}
                    cost={`$${(isWinner ? event.cost : (primaryModel?.inputCost ?? 0) * 0.032).toFixed(3)}`}
                    isWinner={phase === 'decided' && isWinner}
                    isEvaluating={phase === 'evaluating'}
                    delay={idx * 0.2}
                    color={provider.color}
                    borderColor={provider.borderColor}
                  />
                );
              })}
            </div>
          )}
        </AnimatePresence>

        {/* Decision */}
        {phase === 'decided' && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
            className="space-y-1 pt-2 border-t border-gray-800/30"
          >
            <div className="text-[11px]">
              <span className="text-gray-500">Decision: </span>
              <span className="text-amber-400">{event.selectedProvider} {event.selectedModel}</span>
              <span className="text-gray-600"> (${event.cost.toFixed(3)})</span>
            </div>
            <div className="text-[11px]">
              <span className="text-gray-500">Reason: </span>
              <span className="text-gray-400">{event.reason}</span>
            </div>
            {savingsPercent > 0 && (
              <div className="text-[11px]">
                <span className="text-gray-500">Savings: </span>
                <span className="text-green-400">{savingsPercent}%</span>
                <span className="text-gray-600"> vs Anthropic baseline</span>
              </div>
            )}
            {savingsPercent === 0 && (
              <div className="text-[11px]">
                <span className="text-gray-500">Savings: </span>
                <span className="text-gray-400">0%</span>
                <span className="text-gray-600"> (quality &gt; cost for this task)</span>
              </div>
            )}
            {event.fallback && (
              <div className="text-[10px] text-gray-600">
                Fallback: {event.fallback}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};
